'use client';

import { fetchBnplDisplayProducts } from '@/api/pay';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import React, { useEffect, useRef, useState } from 'react';
import { IS_DE, IS_US } from '@/utils/constants';
import { useCurrencySettled } from '@/hooks/computed';
import Image from '@/components/Image';

const formatBnplText = (text: string | null): React.ReactNode => {
  if (!text) return null;
  const regex = /(klarna|paypal|afterpay|clearpay)/gi;
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, index) => {
        const lowerPart = part.toLowerCase();
        if (['klarna', 'paypal', 'afterpay', 'clearpay'].includes(lowerPart)) {
          let src = '';
          switch (lowerPart) {
            case 'klarna':
              src = '/goods-detail/klarna.png';
              break;
            case 'paypal':
              src = '/goods-detail/paypal.png';
              break;
            case 'afterpay':
              src = '/goods-detail/afterpay.png';
              break;
            case 'clearpay':
              src = '/goods-detail/clearpay.png';
              break;
          }
          return (
            <Image
              key={index}
              src={src}
              alt={`${lowerPart} payment`}
              width={50}
              height={12}
              style={{ height: 'max(.8333333333vw, 12px)' }}
              className="w-auto align-middle mx-[2px]"
            />
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
};

/**
 * 需求文档
 * https://alidocs.dingtalk.com/i/nodes/G1DKw2zgV2RwbQLOUPwnLMK0VB5r9YAn?utm_scene=team_space
 */
export const useBnplDisplayProducts = ({ scene, amount }) => {
  const currency = useStore((state) => state.currency);
  const currencyReady = useCurrencySettled();
  const [bnplText, setBnplText] = useState<React.ReactNode | null>(null);
  const [bnplProducts, setBnplProducts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [bnplShow, setBnplShow] = useState(true);
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    let cancelled = false;

    const isCurrentRequest = () => !cancelled && latestRequestIdRef.current === requestId;

    const params = {
      scene,
      country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID.toUpperCase(),
      currency: currency.name?.toUpperCase() || '',
      amount,
    };
    // 德国站不展示
    if (IS_DE) {
      setBnplText(null);
      setBnplProducts([]);
      setBnplShow(false);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    if (IS_US) {
      setBnplText(null);
      setBnplShow(true);
      setLoading(false);
      setError(null);
      setBnplProducts([]);
      return () => {
        cancelled = true;
      };
    }

    if (!scene || !currencyReady) {
      setBnplText(null);
      setBnplShow(false);
      setLoading(false);
      setError(null);
      setBnplProducts([]);
      return () => {
        cancelled = true;
      };
    }

    const loadProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchBnplDisplayProducts(params);

        if (!isCurrentRequest()) {
          return;
        }

        const res = response.data || [];
        const products = res?.products?.map((item) => item?.display_name);

        setBnplProducts(products || []);
        if (products?.length && res?.bnplText) {
          setBnplText(formatBnplText(res.bnplText));
          setBnplShow(true);
        } else {
          setBnplText(null);
          setBnplShow(false);
        }
      } catch (requestError) {
        if (!isCurrentRequest()) {
          return;
        }

        setBnplProducts([]);
        setBnplText(null);
        setBnplShow(false);
        setError(requestError);
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
        }
      }
    };

    void loadProducts();
    return () => {
      cancelled = true;
    };
  }, [amount, currency.name, currencyReady, scene]);

  return {
    bnplProducts,
    bnplText,
    loading,
    error,
    bnplShow,
  };
};
